<template lang="pug">
VehicleLeasingForm(
	ref="form",
	:default-debtor-type="defaultDebtorType",
	:default-vehicle-designation="defaultVehicleDesignation",
	:debtor-type="debtorType",
	:vehicle-designation="vehicleDesignation",
	:finance-type="financeType",
	:vehicle-used="vehicleUsed",
	:vehicle-source="vehicleSource",
	:insurance-type="insuranceType",
	:insurance-own-risk="insuranceOwnRisk",
	:leasing-type="leasingType",
	:personal-period-number="personalPeriodNumber",
	:enterprise-period-number="enterprisePeriodNumber",
	:vehicle-manufacturer-to-designation="vehicleManufacturerToDesignation",
	:insurance-own-contribution="insuranceOwnContribution",
	:resultState="resultState",
	:calculations="calculations",
	:captcha-img="captcha.htmlFragment",
	:captcha-error="captchaError",
	:loading="loading",
	:result-init-tab-index="resultInitTabIndex",
	:sending="sending",
	@captcha-keyup="resetCaptchaError(); resetResults()",
	@form-changed="resetResults",
	@submit="send"
)
	template(slot="disclaimer-text")
		slot(name="disclaimer-text")
	template(slot="personal-info")
		slot(name="personal-info")
	template(slot="enterprise-info")
		slot(name="enterprise-info")
</template>
<script>
import query from 'query-string';
import moment from 'moment';
import VehicleLeasingForm from './vehicle-leasing-form/vehicle-leasing-form';
import vehicleLeasingCalculatorApi from '../../api/vehicle-leasing-calculator-api';
import { FORMATTING_CONSTANTS } from '@ui-common/sf-js-utils';
import {
	FORM_DEFAULTS,
	QUERY_PARAMS,
	RESULTSTATES,
	CALCULATOR_TYPES,
	INCLUDED_INSURANCE_FIELDS,
	OPEN_LEASING_FIELDS,
	CALCULATION_RETURN_CODES,
	INTEREST_TYPES
} from './constants';
import messages from './vehicle-leasing-calculator.messages.json';
const DEFAULT_PUBLIC_ERROR_MESSAGE = 'A kalkuláció során hiba történt, kérjük, próbálja meg újra!';
const BACKEND_YEAR_MONTH_FORMAT = 'YYYY-MM';

export default {
	data: () => ({
		sending: false,
		loading: false,
		defaultDebtorType: '',
		defaultVehicleDesignation: '',
		technicalError: false,
		captcha: {
			captchaKey: null,
			htmlFragment: null
		},
		captchaError: false,
		debtorType: [],
		vehicleDesignation: [],
		financingTypes: [],
		vehicleUsed: [],
		vehicleSource: [],
		insuranceType: [],
		insuranceOwnRisk: [],
		leasingType: [],
		personalPeriodNumber: [],
		enterprisePeriodNumber: [],
		vehicleManufacturerToDesignation: {},
		insuranceOwnContribution: [],
		calculations: {},
		resultState: RESULTSTATES.FORM,
		resultInitTabIndex: 0,
		rawCalculations: [],
		selectedPeriodNumber: null,
		financeType: null
	}),
	components: {
		VehicleLeasingForm
	},
	i18n: { messages },
	methods: {
		resetResults() {
			this.resultState = RESULTSTATES.FORM;
		},
		resetCaptchaError() {
			this.captchaError = false;
		},
		async init() {
			try {
				const response = await vehicleLeasingCalculatorApi.init();
				this.captcha = response.data.captcha;
				this.debtorType = response.data.debtorType;
				this.vehicleDesignation = response.data.vehicleDesignation;
				this.vehicleUsed = response.data.vehicleUsed;
				this.vehicleSource = response.data.vehicleSource;
				this.insuranceType = response.data.insuranceType;
				this.insuranceOwnRisk = response.data.insuranceOwnRisk;
				this.leasingType = response.data.leasingType;
				this.personalPeriodNumber = response.data.personalPeriodNumber;
				this.enterprisePeriodNumber = response.data.enterprisePeriodNumber;
				this.vehicleManufacturerToDesignation = response.data.vehicleManufacturerToDesignation;
				this.insuranceOwnContribution = response.data.insuranceOwnContribution;
				this.financeType = response.data.financeType;
			} catch (err) {
				this.technicalError = true;
			}
		},
		async send(data) {
			try {
				this.sending = true;
				this.resultState = RESULTSTATES.FORM;
				this.loading = true;
				this.$VueLiveAnnouncer.set(this.$t('calculationLoading'));
				this.captchaError = false;
				const dataToSend = {
					captchaKey: this.captcha.captchaKey,
					...this.prepareModel(data)
				};
				const response = await vehicleLeasingCalculatorApi.submit(dataToSend);
				if (response.data && response.data.calculations) {
					this.selectedPeriodNumber = data.model.periodNumber;
					this.rawCalculations = response.data.calculations;
					this.setResult();
				} else {
					this.resultState = RESULTSTATES.ERROR;
					this.technicalError = true;
					this.$VueLiveAnnouncer.set(this.$t('calculationError'));
				}
				this.$store.dispatch('calculation-email/setCalculatorType', CALCULATOR_TYPES.CAR_LEASING);
				this.$store.dispatch('calculation-email/setDuration', data.model.periodNumber);
			} catch (err) {
				if (err.response.status == 400 && err.response.data.error == 'captchaText') {
					this.captchaError = true;
				} else {
					this.technicalError = true;
					this.$VueLiveAnnouncer.set(this.$t('calculationError'));
				}
			} finally {
				this.sending = false;
				this.$refs.form.resetCaptcha();
				this.generateCaptcha();
				this.loading = false;
			}
		},
		async generateCaptcha() {
			try {
				const response = await vehicleLeasingCalculatorApi.generateCaptcha();
				this.captcha = response.data;
			} catch (err) {
				this.technicalError = true;
			}
		},
		prepareModel(data) {
			const { model, type } = data;
			const copyModel = JSON.parse(JSON.stringify(model));
			copyModel.dateOfManufacture = moment(model.dateOfManufacture, FORMATTING_CONSTANTS.DISPLAY_DATE[this.$i18n.locale]).format(
				BACKEND_YEAR_MONTH_FORMAT
			);
			if (!type.includedInsurance) {
				INCLUDED_INSURANCE_FIELDS.forEach(field => {
					delete copyModel[field];
				});
			}
			if (!type.openEnterpriseLeasing) {
				OPEN_LEASING_FIELDS.forEach(field => {
					delete copyModel[field];
				});
			}
			return copyModel;
		},
		setResult() {
			const failedCalculations = this.rawCalculations.filter(
				calculation =>
					calculation.periodNumber == this.selectedPeriodNumber &&
					(calculation.returnCode == CALCULATION_RETURN_CODES.ERROR || calculation.returnCode == CALCULATION_RETURN_CODES.TECHNICAL_ERROR)
			);
			if (failedCalculations.length <= Object.keys(INTEREST_TYPES).length) {
				this.calculations = this.convertCalculations(this.rawCalculations);
				this.resultInitTabIndex = Object.keys(this.calculations).indexOf(this.selectedPeriodNumber);
				this.resultState = RESULTSTATES.SUCCESS;
				this.$VueLiveAnnouncer.set(this.$t('calculationSuccess'));
			} else {
				this.resultState = RESULTSTATES.CALCULATION_ERROR;
			}
		},
		convertCalculations(calculations) {
			const result = {};
			calculations.forEach((calculation, index) => {
				const currentPeriod = calculation.periodNumber;
				if (!result[currentPeriod]) {
					result[currentPeriod] = [];
				}
				calculation.fullPrice = currentPeriod * calculation.instalment + '';
				if (calculation.returnCode == CALCULATION_RETURN_CODES.TECHNICAL_ERROR) {
					calculation.publicErrorMessage = DEFAULT_PUBLIC_ERROR_MESSAGE;
				}
				result[currentPeriod].push(calculation);
			});
			return result;
		},
		setFormDefaults() {
			const params = query.parse(window.location.search.substring(1));
			this.defaultDebtorType = params[QUERY_PARAMS.DEBTOR_TYPE] || FORM_DEFAULTS.DEBTOR_TYPE;
			this.defaultVehicleDesignation = params[QUERY_PARAMS.VEHICLE_DESIGNATION] || FORM_DEFAULTS.VEHICLE_DESIGNATION;
		}
	},
	created() {
		this.setFormDefaults();
		this.init();
	}
};
</script>

<style lang="scss" scoped>
@import '@ui-common/sf-style-utils/lib/helpers/_queries.scss';

.v-leasing-form {
	@include media('<tablet') {
		margin-left: -8px;
		margin-right: -8px;
	}
}
</style>
